/**
 * 봉인 프레젠테이션 뷰모델.
 *
 * 새 구조("시간이 지나면 자라고, 정답을 먹으면 줄어든다")에서 플레이어가 화면에서 읽어야 하는 것은
 * 두 가지고, 둘은 절대 섞이면 안 된다.
 *
 *   압박(pressure) — 필드가 나를 조여온다. 캔버스 **필드 테두리 링**이 소유한다.
 *   진행(progress) — 8문제 중 몇 개를 봉인했다. DOM **상단 띠 도장 8칸**이 소유한다.
 *
 * 위치(테두리 vs 띠)·방향(차오름 vs 채워짐)·형태(연속 띠 vs 이산 블록)로 세 겹 분리한다.
 * 색으로만 구분되는 정보는 여기에도 0개다.
 *
 * ADR-008 — 이 파일의 어떤 값도 **아직 먹지 않은 타일**의 정체에 의존하지 않는다.
 * 링은 논리 길이와 성장 타이머의 함수이고, 도장은 이미 닫힌 문제의 결과다.
 * 섭취 **후** 결과 표시는 상단 띠 슬롯이 이미 하고 있으므로 새로 새는 채널이 아니다.
 */
import type { EngineRules, EngineState, LenLedger } from '../engine';

export type { LenLedger } from '../engine';

// ─────────────────────────────────────────────────────────────────────────────
// 상수 — 전부 11×18(198칸) 기하에서 실측으로 뽑았다(도달가능 영역 시뮬레이션 · 표본 21,600틱/길이).
// ─────────────────────────────────────────────────────────────────────────────

export const SEAL_VIEW = {
  /**
   * 위험 티어 경계(논리 길이, cells). SNAKE_LEN_MAX 40 기준(SEAL §2).
   * 22 = 상한의 55%(정답 4개분 여유) — "슬슬 조인다", 32 = 80%(정답 2개분) — "다음 단어를 놓치면 끝난다".
   * 도달가능 영역 p05 판정(구 32/48)은 상한 67 시절 값이라 40 에서는 티어 2 가 영원히 안 온다.
   */
  LEN_TIER1: 22,
  LEN_TIER2: 32,

  /** 티어 2 맥동 반주기(틱). 명목 135ms 틱에서 4틱 = 540ms → 0.93Hz. WCAG 2.3.1(3Hz) 하한을 넘지 않는다. */
  CRIT_PULSE_TICKS: 4,

  /** 사망 시 포켓 틴트 상한(칸). 이 수를 넘으면 "갇혀 죽은 것"이 아니므로 아예 칠하지 않는다. */
  POCKET_TINT_MAX: 24,

  /** 사망 점멸 — 실시간 60ms × 12스텝. 4스텝 on/off = 480ms 주기 = 2.1Hz. */
  DEATH_BLINK_MS: 60,
  DEATH_BLINK_STEPS: 12,
  DEATH_BLINK_HALF: 4,

  /** 봉인 성공 연출 20틱의 비트 경계 */
  SEAL_TICKS: 20,
  SEAL_DRAIN_TICKS: 6, // 0-5   링이 위쪽 중앙에서 아래쪽 중앙으로 되감긴다
  SEAL_BURN_FROM: 4, // 4-15  꼬리부터 몸이 소진된다
  SEAL_BURN_TICKS: 12,
  SEAL_DOTS_FROM: 12, // 12-19 필드 도트가 GOOD 로 바뀐다

  /**
   * 프레젠테이션이 감당하는 논리 길이 상한 = SNAKE_LEN_MAX(40). 실측식 43 + 길이 <= 110 (tools/drawbudget.ts)
   * 은 67 까지 허용하지만 규칙이 40 에서 판을 끝내므로 예산도 40 에 고정한다(assertEngineRules 가 상한을 지킨다).
   */
  LEN_MAX_BUDGET: 40,
} as const;

export type Tier = 0 | 1 | 2;

/** 길이 원장(`LenLedger`)은 엔진이 소유한다 — 모든 항은 **부호 있는 칸 수**, 표시 순서 고정, 크기순 정렬 금지. */
export const EMPTY_LEDGER: LenLedger = { init: 0, byTime: 0, byAnswer: 0, byWord: 0, byWrong: 0 };

/**
 * 링·원장이 엔진에서 읽는 값. 실제 판은 `extrasFromState()` 로 `EngineState` 에서 직접 뽑고,
 * `fallbackExtras()` 는 성장 시계를 끈 정지 링(테스트·정적 미리보기)용이다.
 */
export interface SealExtras {
  readonly growPeriodTicks: number; // 0 = 성장 시계 정지(크리프 없음)
  readonly growTicksToNext: number;
  readonly calmTicksLeft: number; // 단어 완성 직후 감속 구간
  readonly sealTicksLeft: number; // 봉인 성공 연출 잔여 틱 (0 = 미진행)
  readonly lenMin: number;
  readonly lenMax: number;
  readonly ledger: LenLedger;
}

export type FieldSkin = 'normal' | 'warn' | 'calm' | 'sealed';

export interface SealView {
  readonly logicalLen: number;
  readonly lenMax: number;
  readonly sat: number; // 0..1 포화도
  readonly ringFill: number; // 0..1 링 채움 (포화도 + 성장 크리프)
  readonly tier: Tier;
  readonly critOn: boolean; // 티어 2 맥동 위상
  readonly skin: FieldSkin;
  readonly sealed: number; // 봉인 성공한 문제 수
  readonly failed: number; // 닫혔지만 실패한 문제 수
  readonly total: number;
  readonly stamps: readonly Stamp[]; // 길이 = total
  readonly sealStep: number; // 성공 연출 경과 틱 (-1 = 미진행)
  readonly ledger: LenLedger;
}

export type Stamp = 'sealed' | 'failed' | 'current' | 'todo';

const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);

export function tierOf(logicalLen: number): Tier {
  if (logicalLen >= SEAL_VIEW.LEN_TIER2) return 2;
  if (logicalLen >= SEAL_VIEW.LEN_TIER1) return 1;
  return 0;
}

export function saturation(logicalLen: number, lenMin: number, lenMax: number): number {
  const span = lenMax - lenMin;
  return span <= 0 ? 0 : clamp01((logicalLen - lenMin) / span);
}

/**
 * 링 채움 = 포화도 + **다음 성장까지의 연속 크리프 1노치**.
 * 크리프가 있어야 (a) 성장이 무작위가 아니라 카운트다운으로 읽히고
 * (b) 성장 주기가 짧아지는 것(=가속)이 링이 빨라지는 것으로 그대로 보인다.
 * 별도의 속도 표시기를 두지 않는 근거가 이 한 줄이다.
 */
export function ringFillOf(sat: number, notch: number, period: number, toNext: number): number {
  if (period <= 0) return clamp01(sat);
  const elapsed = period - toNext;
  const creep = elapsed <= 0 ? 0 : elapsed >= period ? 1 : elapsed / period;
  return clamp01(sat + creep * notch);
}

/** 성장 시계를 끈 정지 링. 원장은 엔진 것을 그대로 쓴다. */
export function fallbackExtras(s: EngineState, lenMin: number, lenMax: number): SealExtras {
  return {
    growPeriodTicks: 0,
    growTicksToNext: 0,
    calmTicksLeft: 0,
    sealTicksLeft: 0,
    lenMin,
    lenMax,
    ledger: s.ledger,
  };
}

/**
 * 엔진 상태에서 링 입력을 뽑는다(SEAL §5.6). 성장 주기는 **현재 문제 인덱스**의 것이고,
 * 감속(growPauseLeft) 중에는 growTicks 가 멈춰 있으므로 크리프도 그 자리에 선다.
 */
export function extrasFromState(s: EngineState, rules: EngineRules): SealExtras {
  const period = rules.growPeriodTable[s.word.index] ?? 0;
  return {
    growPeriodTicks: period,
    growTicksToNext: Math.max(0, period - s.growTicks),
    calmTicksLeft: s.growPauseLeft,
    sealTicksLeft: 0,
    lenMin: rules.snakeLenMin,
    lenMax: rules.snakeLenMax,
    ledger: s.ledger,
  };
}

export function deriveSealView(s: EngineState, x: SealExtras, total: number): SealView {
  const logicalLen = s.snake.cells.length + s.snake.pending;
  const sat = saturation(logicalLen, x.lenMin, x.lenMax);
  const span = x.lenMax - x.lenMin;
  const notch = span <= 0 ? 0 : 1 / span;
  const tier = tierOf(logicalLen);

  let sealed = 0;
  let failed = 0;
  for (const r of s.records) {
    if (r.outcome === 'solved') sealed++;
    else failed++;
  }

  const stamps: Stamp[] = [];
  for (let i = 0; i < total; i++) {
    const rec = s.records[i];
    if (rec !== undefined) stamps.push(rec.outcome === 'solved' ? 'sealed' : 'failed');
    else if (i === s.records.length && s.phase === 'play') stamps.push('current');
    else stamps.push('todo');
  }

  const sealStep = x.sealTicksLeft > 0 ? SEAL_VIEW.SEAL_TICKS - x.sealTicksLeft : -1;

  // 스킨 우선순위: 봉인 연출 > 페널티 > 감속. 페널티(일시적)와 위험 티어(지속적)는
  // 같은 채널을 쓰지 않는다 — 티어는 링이, 페널티는 바닥이 가져간다.
  const skin: FieldSkin =
    sealStep >= SEAL_VIEW.SEAL_DOTS_FROM
      ? 'sealed'
      : s.penalty.status === 2
        ? 'warn'
        : x.calmTicksLeft > 0
          ? 'calm'
          : 'normal';

  return {
    logicalLen,
    lenMax: x.lenMax,
    sat,
    ringFill: ringFillOf(sat, notch, x.growPeriodTicks, x.growTicksToNext),
    tier,
    critOn: ((s.tick / SEAL_VIEW.CRIT_PULSE_TICKS) | 0) % 2 === 0,
    skin,
    sealed,
    failed,
    total,
    stamps,
    sealStep,
    ledger: x.ledger,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// 링 기하 — 아래쪽 중앙에서 좌·우 두 팔이 올라가 **위쪽 중앙**(문제 띠 바로 밑)에서 만난다.
// 팔이 둘이라 1노치 이동량이 (fieldW+2t+fieldH)/노치수 로 커진다(360px 폰·dpr3 실측 21.4 CSS px).
// 8팔(네 모서리) 안은 노치당 4~7 CSS px 밖에 안 움직여 "정답을 먹어 링이 물러났다"가 안 보인다.
// ─────────────────────────────────────────────────────────────────────────────

export interface RingRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface RingGeom {
  readonly originX: number;
  readonly originY: number;
  readonly fieldW: number;
  readonly fieldH: number;
  readonly thickness: number;
}

/**
 * 채움 비율 `fill` 에 해당하는 링 사각형들을 `out` 에 채우고 개수를 돌려준다.
 * 팔당 최대 3장 × 2팔 = **최대 6 드로우 콜**. 프레임마다 배열을 새로 만들지 않는다.
 */
export function ringRects(g: RingGeom, fill: number, out: RingRect[]): number {
  const f = clamp01(fill);
  if (f <= 0) return 0;
  const { originX: ox, originY: oy, fieldW: fw, fieldH: fh, thickness: t } = g;
  // 트랙은 벽 띠의 **바깥 사각형**이다. 가로 반쪽은 모서리 정사각형 t 를 포함한다 —
  // 포함하지 않으면 두 팔이 위쪽 중앙에서 2t 만큼 못 만나 링이 영원히 닫히지 않는다.
  const halfW = fw / 2 + t;
  const arm = 2 * halfW + fh;
  const d = f * arm;
  const xc = ox + fw / 2;
  const yBot = oy + fh;
  let n = 0;

  for (const sign of [1, -1] as const) {
    // 1) 아래 변 — 아래쪽 중앙에서 좌/우 모서리로
    const s1 = Math.min(d, halfW);
    if (s1 > 0) out[n++] = { x: sign > 0 ? xc : xc - s1, y: yBot, w: s1, h: t };
    // 2) 옆 변 — 아래에서 위로
    const s2 = Math.min(Math.max(d - halfW, 0), fh);
    if (s2 > 0) out[n++] = { x: sign > 0 ? ox + fw : ox - t, y: yBot - s2, w: t, h: s2 };
    // 3) 위 변 — 좌/우 모서리에서 위쪽 중앙으로. 마지막 한 칸이 문제 띠 바로 아래에서 닫힌다.
    const s3 = Math.min(Math.max(d - halfW - fh, 0), halfW);
    if (s3 > 0) {
      out[n++] = { x: sign > 0 ? ox + fw + t - s3 : ox - t, y: oy - t, w: s3, h: t };
    }
  }
  return n;
}

/**
 * 티어별 링 두께. 링은 **항상 벽 두께 전부**를 쓴다 —
 * 티어 0 을 절반 두께로 얇게 그렸더니 판 시작 시점(채움 7%)에 링이 아예 안 보였고,
 * 그러면 나중에 굵어지는 것이 "게이지가 찼다"가 아니라 "없던 UI 가 생겼다"로 읽힌다(눈검사 실측).
 * 티어 2 만 **바깥쪽으로** 반 칸 더 두꺼워진다. 안쪽으로 키우면 플레이 가능 영역이
 * 줄어든 것처럼 보이는데 실제로는 안 줄었으므로 그건 화면이 규칙을 속이는 것이다.
 */
export function ringThickness(tier: Tier, wall: number): number {
  return tier === 2 ? wall + Math.max(1, Math.round(wall / 2)) : wall;
}

/**
 * 티어별 머리 테두리 두께(cell 분모). 중심시야에 있는 유일한 티어 채널이라
 * 세 단계 모두 **기하로** 갈린다. 색(INK→ACCENT→BAD)은 그 위에 얹는 2차 신호다.
 */
export function headRimDivisor(tier: Tier): number {
  return tier === 2 ? 8 : tier === 1 ? 10 : 12;
}
